import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import Header from '../components/Header';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Divider from '../components/Divider';
import {
    Box,
    Container,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    Button,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { FONT_FAMILY, app, getVideoSrc } from '../constants/constants';
import { useTranslation } from 'react-i18next';

export default function FiveDrillsManychatPage() {
    const [openDialog, setOpenDialog] = useState(null);
    const videoRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t, i18n } = useTranslation();

    const drills = [
        //2on2 ball screen defense
        {
            id: 1,
            title: t('FiveDrillsManychatPage02'),
            description: t('FiveDrillsManychatPage03'),
            image: '/images/drill-1-2on2-ball-screen-def.png',
            videoUrl: getVideoSrc('drill-1-2on2-ball-screen-def', i18n.language)
        },
        //1on1 level the ball
        {
            id: 2,
            title: t('FiveDrillsManychatPage04'),
            description: t('FiveDrillsManychatPage05'),
            image: '/images/drill-2-level-ball.png',
            videoUrl: getVideoSrc('drill-2-level-ball', i18n.language)
        },
        //3on3 stop cuts
        {
            id: 3,
            title: t('FiveDrillsManychatPage06'),
            description: t('FiveDrillsManychatPage07'),
            image: '/images/drill-3-stop-cuts.png',
            videoUrl: getVideoSrc('drill-3-stop-cuts', i18n.language)
        },
        //1on1 defensive recovery
        {
            id: 4,
            title: t('FiveDrillsManychatPage08'),
            description: t('FiveDrillsManychatPage09'),
            image: '/images/drill-4-recuperacion-defensiva.png',
            videoUrl: getVideoSrc('drill-4-recuperacion-defensiva', i18n.language)
        },
        //2on2 gaps and stunts
        {
            id: 5,
            title: t('FiveDrillsManychatPage10'),
            description: t('FiveDrillsManychatPage11'),
            image: '/images/drill-5-2on2-gaps-and-stunts.png',
            videoUrl: getVideoSrc('drill-5-2on2-gaps-and-stunts', i18n.language)
        },
    ];

    const handleOpenDialog = (drillId) => {
        setOpenDialog(drillId);
    };

    const handleCloseDialog = () => {
        setOpenDialog(null);
    };

    const currentDrill = drills.find(drill => drill.id === openDialog);

    // Reproducir video automáticamente cuando se abre el diálogo
    useEffect(() => {
        if (openDialog !== null && videoRef.current) {
            videoRef.current.play().catch(error => {
                // Manejar errores de autoplay (algunos navegadores pueden bloquearlo)
                console.log('Error al reproducir automáticamente:', error);
            });
        }
    }, [openDialog]);

    return (
        <>
            <Layout>
                <Header />
                <Box
                    component="section"
                    sx={{
                        py: { xs: 6, md: 8 },
                        bgcolor: 'white',
                        minHeight: '100vh',
                    }}
                >
                    <Container maxWidth="lg">
                        {/* Título general */}
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: FONT_FAMILY,
                                fontSize: { xs: '2.5rem', md: '3.5rem' },
                                fontWeight: 900,
                                color: '#1a202c',
                                mt: { xs: 4, md: 6 },
                                mb: { xs: 4, md: 6 },
                                textAlign: 'center',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            {t('FiveDrillsManychatPage00')}
                        </Typography>

                        {drills.map((drill, index) => (
                            <React.Fragment key={drill.id}>
                                <Box
                                    sx={{
                                        mb: { xs: 6, md: 8 },
                                        textAlign: { xs: 'center', md: 'left' }
                                    }}
                                >
                                    {/* Título */}
                                    <Typography
                                        variant="h2"
                                        sx={{
                                            fontFamily: FONT_FAMILY,
                                            fontSize: { xs: '1.75rem', md: '2.5rem' },
                                            fontWeight: 800,
                                            color: '#1a202c',
                                            mb: 2,
                                        }}
                                    >
                                        {drill.title}
                                    </Typography>

                                    {/* Descripción */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: FONT_FAMILY,
                                            fontSize: { xs: '1rem', md: '1.1rem' },
                                            color: '#4a5568',
                                            lineHeight: 1.6,
                                            mb: 4,
                                            maxWidth: { xs: '100%', md: '80%' },
                                            mx: { xs: 'auto', md: 0 }
                                        }}
                                    >
                                        {drill.description}
                                    </Typography>

                                    {/* Imagen con icono de play */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: { xs: '100%', md: '400px' },
                                            mx: { xs: 'auto', md: 0 },
                                            cursor: 'pointer',
                                            '&:hover .play-icon': {
                                                transform: 'translate(-50%, -50%) scale(1.1)',
                                                opacity: 0.9,
                                            },
                                            transition: 'transform 0.3s ease',
                                        }}
                                        onClick={() => handleOpenDialog(drill.id)}
                                    >
                                        <Box
                                            component="img"
                                            src={drill.image}
                                            alt={drill.title}
                                            sx={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                                display: 'block',
                                            }}
                                            onError={(e) => {
                                                // Imagen placeholder si no existe
                                                e.target.src = 'https://via.placeholder.com/800x450?text=Basketball+Drill';
                                            }}
                                        />
                                        <Box
                                            className="play-icon"
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                transformOrigin: 'center center',
                                                color: 'white',
                                                fontSize: { xs: '4rem', md: '5rem' },
                                                opacity: 0.85,
                                                transition: 'all 0.3s ease',
                                                pointerEvents: 'none',
                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                                            }}
                                        >
                                            <PlayCircleOutlineIcon sx={{ fontSize: 'inherit' }} />
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Divider (excepto después del último ejercicio) */}
                                {index < drills.length - 1 && (
                                    <Divider marginTop={4} marginBottom={4} />
                                )}
                            </React.Fragment>
                        ))}
                    </Container>
                </Box>

                {/* Dialog para el video */}
                <Dialog
                    open={openDialog !== null}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: isMobile ? 0 : '8px',
                            bgcolor: '#000',
                        }
                    }}
                >
                    <DialogContent
                        sx={{
                            p: 0,
                            position: 'relative',
                            bgcolor: '#000',
                        }}
                    >
                        <IconButton
                            onClick={handleCloseDialog}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                zIndex: 1,
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                                }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {currentDrill && (
                            <Box
                                component="video"
                                ref={videoRef}
                                src={currentDrill.videoUrl}
                                autoPlay
                                muted
                                controls
                                playsInline
                                sx={{
                                    width: '100%',
                                    height: { xs: '100vh', md: '500px' },
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>

                {/* Botón flotante */}
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 40, md: 60 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        animation: 'pump 1.2s ease-out',
                        '@keyframes pump': {
                            '0%': {
                                transform: 'translateX(-50%) scale(1)',
                            },
                            '25%': {
                                transform: 'translateX(-50%) scale(1.3)',
                            },
                            '50%': {
                                transform: 'translateX(-50%) scale(1)',
                            },
                            '75%': {
                                transform: 'translateX(-50%) scale(1.2)',
                            },
                            '100%': {
                                transform: 'translateX(-50%) scale(1)',
                            },
                        },
                        '&:hover': {
                            transform: 'translateX(-50%) translateY(-4px)',
                            animation: 'none',
                        },
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => window.location.href = app}
                        sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            fontWeight: 700,
                            px: { xs: 3, md: 4 },
                            py: { xs: 1.5, md: 2 },
                            borderRadius: '50px',
                            bgcolor: '#1976d2',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            textTransform: 'none',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: '#1565c0',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
                            },
                        }}
                    >
                        {t('FiveDrillsManychatPage01')}
                    </Button>
                </Box>

                <Footer />
                <LanguageSwitcher />
            </Layout>
        </>
    );
}
